import type {
  AudioTranscriptionBatchStatus,
  AudioTranscriptionJobStatus,
  AudioTranscriptionPhase,
} from '@shared/schemas/audio-transcription';

const AUDIO_PHASE_LABELS: Record<AudioTranscriptionPhase, string> = {
  aligning: 'Transcription',
  completed: 'Terminé',
  diarizing: 'Distinction des intervenants',
  finalizing: 'Finalisation',
  preprocessing: 'Préparation',
  probing: 'Préparation',
  queued: 'En file',
  segmenting: 'Préparation',
  transcribing: 'Transcription',
};

const AUDIO_JOB_STATUS_LABELS: Record<AudioTranscriptionJobStatus, string> = {
  cancel_requested: 'Annulation',
  canceled: 'Annulé',
  dead: 'Échec',
  failed: 'Échec',
  processing: 'Préparation',
  queued: 'En file',
  succeeded: 'Terminé',
};

export const AUDIO_TRANSCRIPTION_ACTIVE_STATUSES = [
  'analyzing',
  'cancel_requested',
  'processing',
  'queued',
] as const satisfies readonly AudioTranscriptionBatchStatus[];

export const AUDIO_TRANSCRIPTION_TERMINAL_STATUSES = [
  'canceled',
  'failed',
  'partially_succeeded',
  'succeeded',
] as const satisfies readonly AudioTranscriptionBatchStatus[];

export function isAudioTranscriptionTerminalStatus(
  status: AudioTranscriptionBatchStatus | string | undefined,
): boolean {
  return (AUDIO_TRANSCRIPTION_TERMINAL_STATUSES as readonly string[]).includes(status ?? '');
}

export function getAudioTranscriptionPhaseLabel(input: {
  phase: AudioTranscriptionPhase;
  status: AudioTranscriptionJobStatus;
}) {
  if (input.status === 'succeeded') {
    return AUDIO_JOB_STATUS_LABELS.succeeded;
  }
  if (input.status === 'failed' || input.status === 'dead') {
    return AUDIO_JOB_STATUS_LABELS[input.status];
  }
  if (input.status === 'canceled' || input.status === 'cancel_requested') {
    return AUDIO_JOB_STATUS_LABELS[input.status];
  }
  if (input.status === 'queued') {
    return AUDIO_JOB_STATUS_LABELS.queued;
  }

  return AUDIO_PHASE_LABELS[input.phase];
}

export function formatAudioDuration(durationMs: number | null | undefined) {
  if (durationMs == null || durationMs < 0) {
    return null;
  }

  const totalSeconds = Math.round(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function getAudioTranscriptionSummary(input: {
  succeededJobs: number;
  totalJobs: number;
}) {
  return `${input.succeededJobs} sur ${input.totalJobs} terminé`;
}

export function getAudioTranscriptionHeader(totalJobs: number) {
  return totalJobs > 1
    ? `Traitement de ${totalJobs} fichiers audio`
    : 'Traitement de 1 fichier audio';
}
